// src/components/insights/insightsFormat.js — number formatting + palette for
// Ask Insights. Formats are decided by the server's measures ("int", "pct",
// "money", …); this only renders them.

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function prettyDayKey(key, withYear) {
    if (!key || typeof key !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(key)) return key || '';
    const [y, m, d] = key.split('-').map(Number);
    const showYear = withYear != null ? withYear : y !== new Date().getFullYear();
    return `${MON[m - 1]} ${d}${showYear ? `, ${y}` : ''}`;
}

const trim1 = (n) => {
    const r = Math.round(n * 10) / 10;
    return Number.isInteger(r) ? String(r) : r.toFixed(1);
};

// Returns { main, unit } so the big headline can set the unit smaller.
export function fmtParts(value, format, suffix) {
    if (value == null || Number.isNaN(value)) return { main: '—', unit: '' };
    const v = Number(value);
    switch (format) {
        case 'pct': return { main: trim1(v), unit: '%' };
        case 'pts': return { main: `${v > 0 ? '+' : v < 0 ? '−' : ''}${trim1(Math.abs(v))}`, unit: ' pts' };
        case 'money': return { main: `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, unit: '' };
        case 'money0': return { main: `$${Math.round(v).toLocaleString('en-US')}`, unit: '' };
        case 'signed': return { main: `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toLocaleString('en-US')}`, unit: suffix || '' };
        case 'decimal': return { main: trim1(v), unit: suffix || '' };
        case 'day': return { main: prettyDayKey(value), unit: '' };
        case 'int':
        default: return { main: Math.round(v).toLocaleString('en-US'), unit: suffix || '' };
    }
}

export function fmt(value, format, suffix) {
    if (format === 'day') return prettyDayKey(value);
    const p = fmtParts(value, format, suffix);
    return `${p.main}${p.unit}`;
}

// Chart palette — the brand ramp, plus the tag colours members already know
// (Strong+ teal, gold from the top badge tier). Fixed values, not theme vars,
// so series stay distinguishable whatever the admin's theme.
export const PALETTE = {
    primary: 'var(--mom-primary, #c33764)',
    rose: '#c1848c',
    blush: '#ddb0b5',
    teal: '#4f7c7a',
    gold: '#b8913f',
    sand: '#d4c4bb',
};
export const SERIES = ['primary', 'rose', 'teal', 'gold', 'sand', 'blush'];
export const colorOf = (key, i = 0) => PALETTE[key] || PALETTE[SERIES[i % SERIES.length]];

// Production theme sets warm-gray == charcoal and cream-dark == cream, so every
// secondary tone and hairline is derived (see reference_live_theme_values).
export const T = {
    ink: 'var(--mom-charcoal)',
    soft: 'color-mix(in srgb, var(--mom-charcoal) 72%, var(--mom-cream))',
    faint: 'color-mix(in srgb, var(--mom-charcoal) 50%, var(--mom-cream))',
    ghost: 'color-mix(in srgb, var(--mom-charcoal) 28%, var(--mom-cream))',
    line: 'color-mix(in srgb, var(--mom-charcoal) 10%, var(--mom-cream))',
    hair: 'color-mix(in srgb, var(--mom-charcoal) 7%, var(--mom-cream))',
    grid: 'color-mix(in srgb, var(--mom-charcoal) 8%, var(--mom-cream))',
    well: 'color-mix(in srgb, var(--mom-cream) 55%, #fff)',
    glass: 'rgba(255,255,255,0.82)',
    good: '#4d7a55',
    goodBg: 'color-mix(in srgb, #7f9a7e 16%, #fff)',
    bad: 'var(--mom-primary-dark, #c05a7a)',
    badBg: 'color-mix(in srgb, var(--mom-primary) 8%, #fff)',
    serif: 'var(--mom-font-serif)',
    sans: 'var(--mom-font-sans)',
    shSm: '0 1px 2px rgba(62,47,47,.04), 0 4px 14px rgba(62,47,47,.05)',
    shMd: '0 2px 6px rgba(62,47,47,.04), 0 14px 40px rgba(62,47,47,.08)',
    shLg: '0 24px 64px rgba(62,47,47,.14), 0 6px 20px rgba(62,47,47,.06)',
};
